// ============================================================================
// "You entered $0. That's fine — which is it?" 2026-09-03, Ben.
//
// A $0 is never wrong. It just has to say WHICH KIND of $0 it is, because the
// four flagged records in the book meant four different things by it, in free
// text no report can read:
//   "This job gets invoiced within the monthly fee"  → normal work, keep it
//   "Not billing customer for this service"          → Courtesy, or Our mistake
//   "Paid Hourly"                                    → the sub is on a timecard
//   (blank)                                          → nobody knows
//
// ⛔ THE FIRST ANSWER ON EACH SIDE KEEPS THE RECORD AS PROJECT WORK. "Covered by
// the monthly fee" is the commonest true meaning and it is not an exception at
// all; a prompt that treats it as one manufactures a reclassification out of an
// ordinary job.
//
// ⛔ IT FIRES ON A TYPED ZERO, NEVER ON A BLANK. A zero is a CLAIM — "there is no
// money here" — and a claim can be questioned. A blank is an unfinished form, and
// interrupting someone mid-typing is how you teach them to dismiss dialogs
// without reading them.
//
// ⛔ AND NEVER ON A NEGATIVE. A negative sub rate is a chargeback, stored negative
// by design (entry-derive.ts), and it is a different question entirely.
// ============================================================================

/** Which box the AM typed the zero into. They have different first answers. */
export type ZeroSide = 'client' | 'sub';

/**
 * Should the prompt open?
 *
 * Takes the RAW string from the input, not a number, because the difference
 * between `''` and `'0'` is the whole rule and `Number('')` is 0.
 */
export function shouldAskAboutZero(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false; // a blank is not a claim
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return false;
  if (n < 0) return false; // a chargeback, not a zero
  return n === 0;
}

/**
 * What the AM can answer, per side. The first entry KEEPS it as project work.
 *
 * ⛔ `division_id` is null on the keep answers, deliberately: they are not
 * classifications, they are explanations of an ordinary record. Only the answers
 * that genuinely change what the record IS carry a division.
 */
export interface ZeroAnswer {
  key: string;
  prompt: string;
  detail: string;
  /** null = stays project work. */
  division_id: string | null;
  /** Written into the note so the meaning survives in words as well as in data. */
  note?: string;
  /** Opens the helper instead of deciding here. */
  opensHelper?: boolean;
}

export const ZERO_ANSWERS: Record<ZeroSide, ZeroAnswer[]> = {
  client: [
    {
      key: 'fee',
      prompt: 'It’s covered by their monthly fee',
      detail: 'Normal work, nothing extra to bill.',
      division_id: null,
      note: 'Covered by their monthly fee',
    },
    {
      key: 'free',
      prompt: 'We chose not to charge',
      detail: 'A courtesy or a goodwill gesture.',
      division_id: 'CONCESSION',
    },
    {
      key: 'ours',
      prompt: 'Our mistake',
      detail: 'We broke it, missed it, or had to redo it.',
      division_id: 'RECOVERY',
    },
    {
      key: 'helper',
      prompt: 'I’m not sure',
      detail: 'Walk me through it.',
      division_id: null,
      opensHelper: true,
    },
  ],
  sub: [
    {
      key: 'timecard',
      prompt: 'Their hours are on their timecard',
      detail: 'They get paid through payroll for this.',
      division_id: null,
      note: 'Hours are on their timecard',
    },
    {
      key: 'included',
      prompt: 'It’s covered by their monthly rate',
      detail: 'Already inside what we pay them.',
      division_id: null,
      note: 'Covered by their monthly rate',
    },
    {
      key: 'wrong',
      prompt: 'The pay is wrong and needs fixing',
      detail: 'The amount we paid was not right.',
      division_id: 'CORRECTION',
    },
    {
      key: 'helper',
      prompt: 'I’m not sure',
      detail: 'Walk me through it.',
      division_id: null,
      opensHelper: true,
    },
  ],
};

/**
 * ⚠️ THE CASE NOBODY LISTED, AND THE ONE THAT WOULD DO REAL DAMAGE.
 *
 * The AM types 0, answers "we chose not to charge", the record becomes a
 * Courtesy — and then they realise it was $250 after all and type that. Without
 * this, the record keeps a classification that is now simply false, and we have
 * manufactured a misclassification out of somebody CORRECTING THEMSELVES.
 *
 * So: a division the prompt set is undone the moment the amount stops being zero.
 * A division the AM chose deliberately on the form is NEVER touched — the whole
 * point is that we only unwind our own inference, not their decision.
 */
export function shouldUndoZeroClassification(
  nextRaw: string,
  divisionWasSetByPrompt: boolean,
): boolean {
  if (!divisionWasSetByPrompt) return false;
  return !shouldAskAboutZero(nextRaw);
}
